import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get, errorMessage } from '@/shared/api/client';
import { Alert, Badge, Button, Card, EmptyState, Input, Skeleton, Table } from '@/shared/ui';
import { formatDateTime } from '@/shared/lib/format';

type Ev = { id: number; occurred_at: string; actor_user_id: number | null; actor_type: string; entity_type: string; entity_id: number | null; action: string; before: unknown; after: unknown; metadata: Record<string, unknown>; correlation_id: string; ip_address: string | null };
type Page = { items: Ev[]; next_before_id: number | null };

/** Administración → Auditoría (§25): consulta de solo lectura + verificación de la cadena de hashes. */
export function AuditPage() {
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [beforeId, setBeforeId] = useState<number | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const params = new URLSearchParams({ limit: '50' }); if (entityType) params.set('entity_type', entityType); if (action) params.set('action', action); if (actor) params.set('actor_user_id', actor); if (beforeId) params.set('before_id', String(beforeId));
  const q = useQuery({ queryKey: ['admin', 'audit', entityType, action, actor, beforeId], queryFn: () => get<Page>(`/admin/audit?${params.toString()}`) });
  const chain = useQuery({ queryKey: ['admin', 'audit', 'chain'], queryFn: () => get<{ ok: boolean; first_inconsistent_id: number | null; events: number }>('/admin/audit/verify-chain'), staleTime: 5 * 60_000 });
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-5)', flexWrap: 'wrap', gap: 'var(--sp-3)' }}>
        <h1>Auditoría</h1>
        <div style={{ fontSize: 'var(--fs-sm)' }}>
          {chain.data && (chain.data.ok ? <Badge tone="ok">Cadena íntegra · {chain.data.events} eventos</Badge> : <Badge tone="rejected">Cadena inconsistente desde #{chain.data.first_inconsistent_id}</Badge>)}
          <Button size="sm" variant="ghost" onClick={() => chain.refetch()} style={{ marginLeft: 8 }}>Verificar</Button>
        </div>
      </div>
      <Card>
        <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', marginBottom: 'var(--sp-3)' }}>
          <Input placeholder="Entidad (user, fund_request…)" value={entityType} onChange={(e) => { setEntityType(e.target.value); setBeforeId(null); }} style={{ width: 220, height: 36 }} aria-label="Entidad" />
          <Input placeholder="Acción (prefijo, p. ej. auth.)" value={action} onChange={(e) => { setAction(e.target.value); setBeforeId(null); }} style={{ width: 220, height: 36 }} aria-label="Acción" />
          <Input placeholder="Actor (id)" value={actor} onChange={(e) => { setActor(e.target.value.replace(/[^0-9]/g, '')); setBeforeId(null); }} style={{ width: 120, height: 36 }} aria-label="Actor" />
        </div>
        {q.isPending && <Skeleton height={80} />}
        {q.isError && <Alert kind="error">{errorMessage(q.error)}</Alert>}
        {q.data && q.data.items.length === 0 && <EmptyState title="Sin eventos para el filtro" />}
        {q.data && q.data.items.length > 0 && (
          <Table>
            <thead><tr><th>ID</th><th>Fecha</th><th>Actor</th><th>Acción</th><th>Entidad</th><th>IP</th><th></th></tr></thead>
            <tbody>
              {q.data.items.map((e) => (
                <>
                  <tr key={e.id}>
                    <td className="num">{e.id}</td><td>{formatDateTime(e.occurred_at)}</td>
                    <td>{e.actor_type}{e.actor_user_id ? ` #${e.actor_user_id}` : ''}</td>
                    <td><code style={{ fontSize: 'var(--fs-xs)' }}>{e.action}</code></td>
                    <td>{e.entity_type}{e.entity_id ? ` #${e.entity_id}` : ''}</td>
                    <td style={{ fontSize: 'var(--fs-xs)' }}>{e.ip_address ?? '—'}</td>
                    <td><Button size="sm" variant="ghost" onClick={() => setOpen(open === e.id ? null : e.id)}>{open === e.id ? 'Ocultar' : 'Detalle'}</Button></td>
                  </tr>
                  {open === e.id && (
                    <tr key={`${e.id}-d`}><td colSpan={7}><pre style={{ fontSize: 'var(--fs-xs)', whiteSpace: 'pre-wrap', margin: 0, background: 'var(--sgr-bg-subtle)', padding: 'var(--sp-3)', borderRadius: 'var(--r-sm)' }}>{JSON.stringify({ before: e.before, after: e.after, metadata: e.metadata, correlation_id: e.correlation_id }, null, 2)}</pre></td></tr>
                  )}
                </>
              ))}
            </tbody>
          </Table>
        )}
        {q.data?.next_before_id && <div style={{ marginTop: 'var(--sp-3)' }}><Button variant="secondary" size="sm" onClick={() => setBeforeId(q.data!.next_before_id)}>Más antiguos</Button></div>}
      </Card>
    </>
  );
}
